// src/pages/Dashboard/BookingCardComponent.jsx
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Collapse,
  Tag,
  Button,
  message,
  Descriptions,
  Modal
} from 'antd'; // <-- Note the addition of Collapse
import { COLORS } from '../../../constants/colors';
import TwilioService from '../../../services/twilio/service.js';
import { useNavigate } from 'react-router-dom';
import BookingsService from '../../../services/bookings/service.js';

// Styled components (same as before)
const BookingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const BookingCollapse = styled(Collapse)`
  && { 
    border-radius: 8px!important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10); 
  }

  .ant-collapse-item {
    border-radius: 8px!important;
    overflow: hidden;
  }

   .ant-collapse-header {
     background-color: #fff;
     display: flex;
     align-items: center;
     justify-content: center!important;
     padding: 20px !important;

     .ant-collapse-expand-icon {
        align-self: center;
     }

     .ant-collapse-arrow {
       margin-left: auto;
       margin-right: auto;
     }
   }

   /* Make panel content white */
   .ant-collapse-content {
     background-color: #fff;
   }
`;

const ClientName = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const ServiceName = styled.h4`
  margin: 4px 0;
  font-size: 16px;
  font-weight: 500;
  color: ${COLORS.$gray700};
`;

const ActionContainer = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

// Assign color based on status
const getStatusColor = (status) => {
  switch (status) {
    case 'upcoming':
      return 'green';
    case 'confirmed':
      return 'cyan';
    case 'completed':
      return 'blue';
    case 'canceled':
      return 'red';
    default:
      return 'gold';
  }
};

// Helper to get timezone abbreviation
const getTimeZoneAbbreviation = (timeZone) => {
  try {
    const date = new Date();
    const options = {
      timeZone,
      timeZoneName: "short",
    };
    const formatter = new Intl.DateTimeFormat("en-US", options);
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find((part) => part.type === "timeZoneName");
    return tzPart?.value || "CST"; // Fallback to "CST"
  } catch (error) {
    console.error("Error fetching timezone abbreviation:", error);
    return "CST";
  }
};

// Updated Helper to format time with timezone
const formatTimeOnly = (isoString, timeZone) => {
  const date = new Date(isoString);
  const timeAbbreviation = getTimeZoneAbbreviation(timeZone || "America/Chicago");
  return (
    date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }) +
    ` ${timeAbbreviation}`
  );
};

// Helper to format just the date (e.g., "Dec 19, 2024")
const formatDateOnly = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Destructure Panel from Collapse
const { Panel } = Collapse;

const BookingCardComponent = ({ booking, refreshBookings }) => {
  const { client, service, startTime, endTime, duration, status, _id } = booking;
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [loadingToken, setLoadingToken] = useState(false);
  const navigate = useNavigate();

  // Trigger the confirmation modal
  const handleMarkCompleted = (e) => {
    e.stopPropagation();
    setConfirmModalVisible(true);
  };

  // Actually mark the booking as completed
  const handleConfirmMarkCompleted = async () => {
    try {
      await BookingsService.updateBooking(_id, { status: 'completed' });
      message.success('Booking marked as completed!');
      if (refreshBookings) {
        refreshBookings(); // <-- Trigger the bookings re-fetch
      }
    } catch (error) {
      message.error('Failed to mark booking as completed');
    } finally {
      setConfirmModalVisible(false);
    }
  };

  // Start the Twilio call
  const handleStartCall = async (e) => {
    e.stopPropagation();
    setLoadingToken(true);
    try {
      // 1. Get room details
      const room = await TwilioService.getRoomDetails(booking.room.roomName);
      if (!room) {
        throw new Error('Room not found');
      }

      // 2. Get video token
      const { token } = await TwilioService.getVideoToken(booking.room.roomName, 'sparkleDefault');

      // 3. Save room details to DB
      const roomData = await TwilioService.createRoomRecord(booking.room.roomName, _id);

      if (roomData && roomData.room) {
        // 4. Navigate to the video call
        navigate(
          `/video-call?roomName=${booking.room.roomName}&token=${token}&username=Meeting-Host&bookingId=${_id}`
        );
      } else {
        message.error('Failed to create room');
      }
    } catch (error) {
      message.error('Failed to start video call');
    } finally {
      setLoadingToken(false);
    }
  };

  return (
    <BookingCollapse style={{ marginBottom: 16 }} accordion>
      <Panel
        key={_id}
        header={
          <BookingHeader>
            <div>
              <ClientName>{service}</ClientName>
            </div>
            {status === 'confirmed' ? (
              <Tag color={getStatusColor(status)}>
                {formatTimeOnly(startTime, booking.timeZone)}
              </Tag>
            ) : (
              <Tag color={getStatusColor(status)}>
                {status.toUpperCase()}
              </Tag>
            )}
          </BookingHeader>
        }
      >
        <ServiceName>{client.name}</ServiceName>
        <h4>{formatDateOnly(startTime)}</h4>
        <Descriptions
          bordered
          size="small"
          column={1}
          style={{ marginTop: 16 }}
        >
          <Descriptions.Item label="Start">
            {formatTimeOnly(startTime, booking.timeZone)}
          </Descriptions.Item>
          <Descriptions.Item label="End">
            {formatTimeOnly(endTime, booking.timeZone)}
          </Descriptions.Item>
          <Descriptions.Item label="Duration">
            {duration} minutes
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {client.email}
          </Descriptions.Item>
        </Descriptions>

        <ActionContainer>
          <Button
            onClick={handleMarkCompleted}
            disabled={status === 'completed'}
          >
            Mark Completed
          </Button>
          <Button
            type="primary"
            onClick={handleStartCall}
            loading={loadingToken}
            disabled={status === 'completed'}
          >
            Start Call
          </Button>
        </ActionContainer>

        <Modal
          title="Confirm Completion"
          visible={confirmModalVisible}
          onOk={handleConfirmMarkCompleted}
          onCancel={() => setConfirmModalVisible(false)}
          okText="Yes, Mark Completed"
          cancelText="Cancel"
        >
          <p>Are you sure you want to mark this booking as completed? This action cannot be undone.</p>
        </Modal>
      </Panel>
    </BookingCollapse >
  );
};

export default BookingCardComponent;
